import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import type {
  ChatMessage,
  ConnectionStatus,
  DevEvent,
  DevInfo,
  IntentHintMessage,
  PreviewConfig,
  QuestionMessage,
  ServerMessage,
  StepInfo,
  WorkflowInfo,
} from '../types/protocol';

const STORAGE_KEY = 'singlepilot_session';
const MAX_PERSISTED_MESSAGES = 100;
const TRANSIENT_ERROR_CODES = new Set([
  'INVALID_CONTEXT',
  'NO_CONTEXT',
  'SKILL_BOOTSTRAP_ERROR',
  'UNAUTHENTICATED',
  'UNKNOWN_CLUSTER',
]);
const TRANSIENT_ERROR_TEXT = [
  'failed to bootstrap skills:',
  'invalid or expired usertoken',
  'no active context',
  'please set context first',
  'set_context requires a signed usertoken',
  'unknown cluster:',
];

let messageCounter = 0;
function nextId(): string {
  return `msg-${Date.now()}-${++messageCounter}`;
}

interface SavedSession {
  messages: ChatMessage[];
  sessionId?: string;
  workflowId?: string;
  workflowName?: string;
  workflowDescription?: string;
  answers?: Record<string, unknown>;
  steps?: StepInfo[];
  previewConfig?: PreviewConfig;
  previewData?: unknown;
  connectionParams?: ConnectionParams;
}

interface ConnectionParams {
  wsUrl: string;
  cluster?: string;
  userId: number;
  userLevel?: number;
  module?: string;
  locale?: string;
  service?: string;
  mcpUrl?: string;
  userInfoUrl?: string;
  userToken?: string;
}

function isTransientErrorText(content: string): boolean {
  const normalized = content.toLowerCase();
  return TRANSIENT_ERROR_TEXT.some((pattern) => normalized.includes(pattern));
}

function isTransientServerError(message: ServerMessage): boolean {
  return message.type === 'error' && (
    (typeof message.code === 'string' && TRANSIENT_ERROR_CODES.has(message.code)) ||
    isTransientErrorText(message.message)
  );
}

function shouldPersistMessage(message: ChatMessage): boolean {
  if (message.streaming) return false;
  if (message.subtype === 'error' && isTransientErrorText(message.content)) return false;
  return true;
}

function sanitizeMessagesForStorage(messages: ChatMessage[]): ChatMessage[] {
  return messages
    .filter(shouldPersistMessage)
    .slice(-MAX_PERSISTED_MESSAGES)
    .map((message) => {
      const { devInfo: _devInfo, ...persistable } = message;
      return persistable;
    });
}

function saveSession(data: SavedSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage might be full or unavailable
  }
}

function loadSession(): SavedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedSession;
    return {
      ...saved,
      messages: sanitizeMessagesForStorage(saved.messages ?? []),
    };
  } catch {
    return null;
  }
}

function clearSavedSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export interface UseChatReturn {
  messages: ChatMessage[];
  steps: StepInfo[];
  connectionStatus: ConnectionStatus;
  sessionId: string | null;
  workflowName: string | null;
  workflowDescription: string | null;
  availableWorkflows: WorkflowInfo[];
  intentHint: IntentHintMessage | null;
  currentQuestion: QuestionMessage | null;
  isThinking: boolean;
  previewConfig: PreviewConfig | null;
  previewData: unknown;
  handleServerMessage: (message: ServerMessage) => void;
  addUserMessage: (text: string) => void;
  clearCurrentQuestion: () => void;
  clearIntentHint: () => void;
  clearChat: () => void;
  getSavedSession: () => SavedSession | null;
  trackAnswer: (field: string, value: unknown) => void;
  setConnectionParams: (params: ConnectionParams) => void;
}

export function useChat(devMode = false): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [steps, setSteps] = useState<StepInfo[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [workflowName, setWorkflowName] = useState<string | null>(null);
  const [workflowDescription, setWorkflowDescription] = useState<string | null>(null);
  const [availableWorkflows, setAvailableWorkflows] = useState<WorkflowInfo[]>([]);
  const [intentHint, setIntentHint] = useState<IntentHintMessage | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<QuestionMessage | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<PreviewConfig | null>(null);
  const [previewData, setPreviewData] = useState<unknown>(null);

  // Track streaming messages by ID
  const streamingRef = useRef<Map<string, string>>(new Map());

  // Track workflow answers for persistence (updated from step_progress)
  const workflowAnswersRef = useRef<Record<string, unknown>>({});
  const workflowIdRef = useRef<string | undefined>();
  const connectionParamsRef = useRef<ConnectionParams | undefined>();

  // Persist session state when messages or workflow state change
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const workflowNameRef = useRef(workflowName);
  workflowNameRef.current = workflowName;
  const workflowDescriptionRef = useRef(workflowDescription);
  workflowDescriptionRef.current = workflowDescription;

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const previewConfigRef = useRef(previewConfig);
  previewConfigRef.current = previewConfig;
  const previewDataRef = useRef(previewData);
  previewDataRef.current = previewData;

  const persistState = useCallback(() => {
    saveSession({
      messages: sanitizeMessagesForStorage(messagesRef.current),
      sessionId: sessionIdRef.current ?? undefined,
      workflowId: workflowIdRef.current,
      workflowName: workflowNameRef.current ?? undefined,
      workflowDescription: workflowDescriptionRef.current ?? undefined,
      answers: workflowAnswersRef.current,
      steps: stepsRef.current,
      previewConfig: previewConfigRef.current ?? undefined,
      previewData: previewDataRef.current ?? undefined,
      connectionParams: connectionParamsRef.current,
    });
  }, []);

  // Debounced persist
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schedulePersist = useCallback(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(persistState, 500);
  }, [persistState]);

  // Persist whenever messages, steps, preview, or session ID change
  useEffect(() => {
    if (messages.length > 0 || sessionId) {
      schedulePersist();
    }
  }, [messages, steps, sessionId, previewConfig, previewData, schedulePersist]);

  const addAssistantMessage = useCallback((content: string, extra?: Partial<ChatMessage>, sourceEvent?: ServerMessage) => {
    const devInfo: DevInfo | undefined = devMode && sourceEvent ? {
      events: [{ type: sourceEvent.type, timestamp: Date.now(), payload: sourceEvent }],
      startedAt: Date.now(),
    } : undefined;
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'assistant' as const,
        content,
        timestamp: Date.now(),
        ...extra,
        ...(devInfo ? { devInfo } : {}),
      },
    ]);
  }, [devMode]);

  const addUserMessage = useCallback((text: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'user' as const,
        content: text,
        timestamp: Date.now(),
      },
    ]);
  }, []);

  const handleServerMessage = useCallback(
    (message: ServerMessage) => {
      switch (message.type) {
        case 'connected': {
          setConnectionStatus('connected');
          // Restore saved chat messages only (workflow state will be restored via restore_session)
          const saved = loadSession();
          if (saved) {
            if (saved.answers) workflowAnswersRef.current = saved.answers;
            if (saved.workflowId) workflowIdRef.current = saved.workflowId;
            if (saved.connectionParams) connectionParamsRef.current = saved.connectionParams;
            setMessages(saved.messages);
            setSessionId(saved.sessionId ?? message.sessionId);
          } else {
            setSessionId(message.sessionId);
            setMessages([]);
          }
          // Don't restore workflow UI state (steps, workflowName) — server will send via restore_session
          setSteps([]);
          setWorkflowName(null);
          setWorkflowDescription(null);
          setCurrentQuestion(null);
          setIntentHint(null);
          setIsThinking(false);
          streamingRef.current.clear();
          break;
        }

        case 'context_set':
          setConnectionStatus('context_set');
          if (message.workflows) {
            setAvailableWorkflows(message.workflows);
          }
          break;

        case 'thinking':
          setIsThinking(true);
          break;

        case 'status':
          setMessages((prev) => [
            ...prev,
            { id: nextId(), role: 'system', content: message.message, timestamp: Date.now() },
          ]);
          break;

        case 'info':
          // Skip duplicate "Connected to" message when restoring a session
          setMessages((prev) => {
            const isDuplicate = prev.some((m) => m.subtype === 'event' && m.content === message.message);
            if (isDuplicate) return prev;
            return [
              ...prev,
              {
                id: nextId(),
                role: 'assistant' as const,
                content: message.message,
                timestamp: Date.now(),
                subtype: 'event' as const,
              },
            ];
          });
          setIsThinking(false);
          break;

        case 'error':
          if (isTransientServerError(message)) {
            setMessages((prev) => {
              if (prev.some((m) => m.subtype === 'error' && m.content === message.message)) {
                return prev;
              }
              return [
                ...prev,
                {
                  id: nextId(),
                  role: 'assistant' as const,
                  content: message.message,
                  timestamp: Date.now(),
                  subtype: 'error' as const,
                },
              ];
            });
          } else {
            addAssistantMessage(message.message, { subtype: 'error' }, message);
          }
          setIsThinking(false);
          break;

        case 'validation_error':
          addAssistantMessage(`**${message.field}**: ${message.message}`, { subtype: 'error' }, message);
          break;

        case 'workflow_activated': {
          setWorkflowName(message.name);
          setWorkflowDescription(message.description);
          setIsThinking(false);
          setIntentHint(null);
          workflowIdRef.current = message.id;

          // When restoring, the server rebuilds step tracker state but lacks
          // resolved option labels (e.g. program name) because the options
          // cache is empty. Merge saved labels from the previous session so
          // completed steps still show human-readable values.
          let steps = message.steps;
          if (message.restored) {
            const saved = loadSession();
            if (saved?.steps) {
              const savedValueMap = new Map<string, unknown>();
              for (const s of saved.steps) {
                if (s.value !== undefined) {
                  savedValueMap.set(s.id, s.value);
                }
              }
              steps = steps.map((s) => {
                if (s.value === undefined && savedValueMap.has(s.id)) {
                  return { ...s, value: savedValueMap.get(s.id) };
                }
                return s;
              });
            }
          }
          setSteps(steps);

          // Set preview configuration if provided
          if (message.preview) {
            setPreviewConfig(message.preview);
            setPreviewData(null);
          }
          // Capture answers for session persistence (includes prefilled values)
          if (message.answers) {
            workflowAnswersRef.current = message.answers;
          }
          // Don't add "Started" message when restoring a session
          if (!message.restored) {
            addAssistantMessage(i18n.t('chat.workflowStarted', { name: message.name }), { subtype: 'event' }, message);
          }
          break;
        }

        case 'workflow_deactivated':
          setSteps([]);
          setWorkflowName(null);
          setWorkflowDescription(null);
          setCurrentQuestion(null);
          setIsThinking(false);
          setPreviewConfig(null);
          setPreviewData(null);
          workflowIdRef.current = undefined;
          workflowAnswersRef.current = {};
          break;

        case 'prefill':
          // Step states are already updated via workflow_activated answers
          break;

        case 'question':
          setCurrentQuestion(message);
          setIsThinking(false);
          if (!message.silent) {
            addAssistantMessage(message.message, { question: message }, message);
          }
          break;

        case 'step_progress':
          setSteps(message.steps);
          break;

        case 'search_results':
          setCurrentQuestion((prev) =>
            prev && prev.stepId === message.stepId
              ? { ...prev, options: message.options }
              : prev,
          );
          break;

        case 'recipe_preview':
          setCurrentQuestion(null);
          setIsThinking(false);
          // Capture all answers from recipe for persistence
          if (message.answers) {
            workflowAnswersRef.current = message.answers;
          }
          addAssistantMessage(i18n.t('chat.recipeIntro'), {
            recipePreview: message,
          }, message);
          break;

        case 'step_result':
          setIsThinking(false);
          setMessages((prev) => {
            // Find the last message with executionSteps
            let lastIdx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i]?.executionSteps) { lastIdx = i; break; }
            }

            const devEvent: DevEvent | undefined = devMode
              ? { type: 'step_result', timestamp: Date.now(), payload: message }
              : undefined;

            // If no execution message exists yet, create one
            if (lastIdx === -1) {
              const newStep = {
                id: message.stepId,
                label: (message as { label?: string }).label ?? message.stepId,
                status: message.status as 'pending' | 'running' | 'success' | 'error',
                message: message.message,
                rollbackable: (message as { rollbackable?: boolean }).rollbackable,
              };
              return [
                ...prev,
                {
                  id: nextId(),
                  role: 'assistant' as const,
                  content: '',
                  timestamp: Date.now(),
                  executionSteps: [newStep],
                  ...(devEvent ? { devInfo: { events: [devEvent], startedAt: Date.now() } } : {}),
                },
              ];
            }

            const updated = [...prev];
            const msg = { ...updated[lastIdx]! };
            const steps = [...(msg.executionSteps ?? [])];

            // Check if step already exists (update) or is new (add)
            const existingIdx = steps.findIndex((s) => s.id === message.stepId);
            if (existingIdx >= 0) {
              steps[existingIdx] = {
                ...steps[existingIdx]!,
                status: message.status as 'pending' | 'running' | 'success' | 'error',
                message: message.message,
                rollbackable: (message as { rollbackable?: boolean }).rollbackable ?? steps[existingIdx]!.rollbackable,
              };
            } else {
              steps.push({
                id: message.stepId,
                label: (message as { label?: string }).label ?? message.stepId,
                status: message.status as 'pending' | 'running' | 'success' | 'error',
                message: message.message,
                rollbackable: (message as { rollbackable?: boolean }).rollbackable,
              });
            }

            msg.executionSteps = steps;
            // Append dev event to existing devInfo
            if (devEvent && msg.devInfo) {
              msg.devInfo = { ...msg.devInfo, events: [...msg.devInfo.events, devEvent] };
            } else if (devEvent) {
              msg.devInfo = { events: [devEvent], startedAt: Date.now() };
            }
            updated[lastIdx] = msg;
            return updated;
          });
          break;

        case 'complete':
          setIsThinking(false);
          addAssistantMessage(message.message, { subtype: 'success', actions: message.actions }, message);
          break;

        case 'intent_hint':
          setIntentHint(message);
          break;

        case 'stream_start':
          streamingRef.current.set(message.id, '');
          setMessages((prev) => [
            ...prev,
            {
              id: message.id,
              role: 'assistant',
              content: '',
              timestamp: Date.now(),
              streaming: true,
              ...(devMode ? { devInfo: {
                events: [{ type: 'stream_start', timestamp: Date.now(), payload: message }],
                startedAt: Date.now(),
              } } : {}),
            },
          ]);
          setIsThinking(false);
          break;

        case 'stream_chunk': {
          const existing = streamingRef.current.get(message.id) ?? '';
          const updated = existing + message.text;
          streamingRef.current.set(message.id, updated);
          setMessages((prev) =>
            prev.map((m) => (m.id === message.id ? { ...m, content: updated } : m)),
          );
          break;
        }

        case 'stream_end':
          streamingRef.current.delete(message.id);
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== message.id) return m;
              const updated: ChatMessage = { ...m, streaming: false };
              if (devMode && m.devInfo) {
                const now = Date.now();
                updated.devInfo = {
                  ...m.devInfo,
                  events: [...m.devInfo.events, { type: 'stream_end', timestamp: now, payload: message }],
                  usage: message.usage,
                  durationMs: m.devInfo.startedAt ? now - m.devInfo.startedAt : undefined,
                };
              }
              return updated;
            }),
          );
          break;

        case 'session_reset':
          setSessionId(message.sessionId);
          break;

        case 'preview_update':
          setPreviewData(message.data);
          setIsThinking(false);
          break;

        case 'rollback_progress':
          setMessages((prev) => {
            // Find the last message with executionSteps
            let lastIdx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i]?.executionSteps) { lastIdx = i; break; }
            }
            if (lastIdx === -1) return prev;

            const updated = [...prev];
            const msg = { ...updated[lastIdx]! };
            const steps = [...(msg.executionSteps ?? [])];
            const existingIdx = steps.findIndex((s) => s.id === message.stepId);
            if (existingIdx >= 0) {
              steps[existingIdx] = {
                ...steps[existingIdx]!,
                status: message.status,
                message: message.message,
              };
            }
            msg.executionSteps = steps;
            updated[lastIdx] = msg;
            return updated;
          });
          break;

        case 'skill_index':
          if (devMode) {
            const skillNames = message.skills.map((s) => s.name).join(', ');
            const content = `Skill index loaded: ${message.skills.length} skills (${skillNames})`;
            // Deduplicate: session restore loads saved messages, then a fresh
            // skill_index arrives on reconnect — skip if already present.
            setMessages((prev) => {
              if (prev.some((m) => m.subtype === 'event' && m.content === content)) return prev;
              const devInfo: DevInfo | undefined = {
                events: [{ type: message.type, timestamp: Date.now(), payload: message }],
                startedAt: Date.now(),
              };
              return [...prev, {
                id: nextId(),
                role: 'assistant' as const,
                content,
                timestamp: Date.now(),
                subtype: 'event' as const,
                devInfo,
              }];
            });
          }
          break;

        case 'skill_activated':
          if (devMode) {
            addAssistantMessage(
              `Skill activated: ${message.skillName} [${message.trigger}]`,
              { subtype: 'event' },
              message,
            );
          }
          break;
      }
    },
    [addAssistantMessage, devMode],
  );

  const clearCurrentQuestion = useCallback(() => {
    setCurrentQuestion(null);
  }, []);

  const clearIntentHint = useCallback(() => {
    setIntentHint(null);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSteps([]);
    setWorkflowName(null);
    setWorkflowDescription(null);
    setCurrentQuestion(null);
    setIntentHint(null);
    setIsThinking(false);
    setPreviewConfig(null);
    setPreviewData(null);
    setSessionId(null);
    streamingRef.current.clear();
    workflowIdRef.current = undefined;
    workflowAnswersRef.current = {};
    clearSavedSession();
  }, []);

  const getSavedSession = useCallback((): SavedSession | null => {
    return loadSession();
  }, []);

  const trackAnswer = useCallback((field: string, value: unknown) => {
    workflowAnswersRef.current[field] = value;
    schedulePersist();
  }, [schedulePersist]);

  const setConnectionParams = useCallback((params: ConnectionParams) => {
    connectionParamsRef.current = params;
    schedulePersist();
  }, [schedulePersist]);

  return {
    messages,
    steps,
    connectionStatus,
    sessionId,
    workflowName,
    workflowDescription,
    availableWorkflows,
    intentHint,
    currentQuestion,
    isThinking,
    previewConfig,
    previewData,
    handleServerMessage,
    addUserMessage,
    clearCurrentQuestion,
    clearIntentHint,
    clearChat,
    getSavedSession,
    trackAnswer,
    setConnectionParams,
  };
}
